import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import './kineticHero.css';

const ASSET_ROOT = '/assets/kinetic-hero';
const MODEL_ROOT = `${ASSET_ROOT}/models`;

const MODEL_ASSETS = {
  robot: `${MODEL_ROOT}/robot-web.glb`,
  gyroscope: `${MODEL_ROOT}/gyroscope-web.glb`,
  orbital: `${MODEL_ROOT}/orbital-web.glb`,
} as const;
const MODEL_LOADING_GRACE_MS = 8_000;
const BLOOM_LAYER = 1;

const DESKTOP_MODEL_X = {
  robot: 0.57,
  gyroscope: 2.85,
  orbital: 5.96,
} as const;

const COMPACT_MODEL_X = {
  robot: 1.12,
  gyroscope: 2.92,
  orbital: 4.72,
} as const;

const MOBILE_MODEL_X = {
  robot: 0.95,
  gyroscope: 2.60,
  orbital: 4.25,
} as const;

type ModelId = 'robot' | 'gyroscope' | 'orbital';
type MotionPart = THREE.Object3D | null;
type HighlightMaterial = {
  material: THREE.MeshStandardMaterial;
  baseEmissive: THREE.Color;
  baseIntensity: number;
  hoverBoost: number;
};

type KineticModel = {
  id: ModelId;
  root: THREE.Group;
  parts: Record<string, MotionPart>;
  glowMaterials: THREE.MeshStandardMaterial[];
  baseSpeed: number;
  speed: number;
  targetSpeed: number;
  phase: number;
  highlightMaterials?: HighlightMaterial[];
  hoverLight?: THREE.PointLight;
  hoverAmount?: number;
  hotspotAnchor?: THREE.Vector3;
};

type KineticHeroProps = {
  onLoadProgress?: (progress: number) => void;
};

const navItems = [
  ['核心项目', 'projects'],
  ['拍/剪作品', 'video'],
  ['AI', 'projects'],
  ['关于我', 'about'],
  ['联系我', 'contact'],
] as const;

function enableShadows(root: THREE.Object3D) {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

function tagModel(root: THREE.Object3D, id: ModelId) {
  root.traverse((object) => {
    object.userData.kineticModelId = id;
  });
}

function findRequiredNode(root: THREE.Object3D, name: string) {
  const node = root.getObjectByName(name);
  if (!node) throw new Error(`Missing required GLB node: ${name}`);
  return node;
}

function setNodeMaterial(node: THREE.Object3D, material: THREE.Material) {
  node.traverse((object) => {
    if (object instanceof THREE.Mesh) object.material = material;
  });
}

function enableBloom(node: THREE.Object3D) {
  node.traverse((object) => object.layers.enable(BLOOM_LAYER));
}

function installNeutralGoldRemap(material: THREE.MeshStandardMaterial, strength: number) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.kineticGoldRemapStrength = { value: strength };
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float kineticGoldRemapStrength;',
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
float kineticMapMax = max(diffuseColor.r, max(diffuseColor.g, diffuseColor.b));
float kineticMapMin = min(diffuseColor.r, min(diffuseColor.g, diffuseColor.b));
float kineticMapLuma = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
float kineticNeutralMask = 1.0 - smoothstep(0.035, 0.16, kineticMapMax - kineticMapMin);
float kineticBrightMask = smoothstep(0.18, 0.68, kineticMapLuma);
vec3 kineticAntiqueGold = vec3(kineticMapLuma * 1.08, kineticMapLuma * 0.62, kineticMapLuma * 0.24);
diffuseColor.rgb = mix(diffuseColor.rgb, kineticAntiqueGold, kineticNeutralMask * kineticBrightMask * kineticGoldRemapStrength);`,
      );
  };
  material.customProgramCacheKey = () => `kinetic-neutral-gold-${strength}`;
  material.needsUpdate = true;
}

function getResponsiveModelLayout(viewportWidth: number) {
  if (viewportWidth <= 700) {
    const mobileBlend = THREE.MathUtils.smoothstep(viewportWidth, 360, 700);
    return {
      robot: THREE.MathUtils.lerp(MOBILE_MODEL_X.robot, COMPACT_MODEL_X.robot, mobileBlend),
      gyroscope: THREE.MathUtils.lerp(MOBILE_MODEL_X.gyroscope, COMPACT_MODEL_X.gyroscope, mobileBlend),
      orbital: THREE.MathUtils.lerp(MOBILE_MODEL_X.orbital, COMPACT_MODEL_X.orbital, mobileBlend),
    } satisfies Record<ModelId, number>;
  }
  const blend = THREE.MathUtils.smoothstep(viewportWidth, 900, 1366);
  return {
    robot: THREE.MathUtils.lerp(COMPACT_MODEL_X.robot, DESKTOP_MODEL_X.robot, blend),
    gyroscope: THREE.MathUtils.lerp(COMPACT_MODEL_X.gyroscope, DESKTOP_MODEL_X.gyroscope, blend),
    orbital: THREE.MathUtils.lerp(COMPACT_MODEL_X.orbital, DESKTOP_MODEL_X.orbital, blend),
  } satisfies Record<ModelId, number>;
}

function tuneOrbitalFixedMaterials(node: THREE.Object3D) {
  node.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const source = Array.isArray(object.material) ? object.material : [object.material];
    const tuned = source.map((entry) => {
      if (!(entry instanceof THREE.MeshStandardMaterial)) return entry;
      const material = entry.clone();
      if (material.map) material.color.set(0x51483e);
      else material.color.set(0x222225);
      material.metalness = 0.88;
      material.roughness = 0.38;
      material.envMapIntensity = 0.68;
      installNeutralGoldRemap(material, 0.72);
      material.needsUpdate = true;
      return material;
    });
    object.material = Array.isArray(object.material) ? tuned : tuned[0];
  });
}

function installOrbitalPanelGlow(node: THREE.Object3D, colorA: number, colorB: number) {
  const glowMaterials: THREE.MeshStandardMaterial[] = [];
  node.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry.computeBoundingBox();
    const bounds = object.geometry.boundingBox;
    if (!bounds) return;
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const axis = size.x >= size.y && size.x >= size.z
      ? new THREE.Vector3(1, 0, 0)
      : size.y >= size.z
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(0, 0, 1);
    const halfExtent = Math.max(size.dot(axis) * 0.5, 0.001);
    const source = Array.isArray(object.material) ? object.material : [object.material];
    const tuned = source.map((entry, materialIndex) => {
      if (!(entry instanceof THREE.MeshStandardMaterial)) return entry;
      const material = entry.clone();
      if (material.map) material.color.set(0xc7a35a);
      else material.color.set(0xd4af37);
      material.metalness = 0.9;
      material.roughness = 0.22;
      material.envMapIntensity = 1.5;
      material.emissive.set(0x000000);
      material.emissiveIntensity = 2.0;
      material.toneMapped = true;
      material.userData.kineticPanelGlow = true;
      const bloomOnlyUniform = { value: 0 };
      const hoverGoldUniform = { value: 0 };
      material.userData.kineticBloomUniform = bloomOnlyUniform;
      material.userData.kineticHoverGoldUniform = hoverGoldUniform;
      material.onBeforeCompile = (shader) => {
        shader.uniforms.kineticPanelCenter = { value: center };
        shader.uniforms.kineticPanelAxis = { value: axis };
        shader.uniforms.kineticPanelHalfExtent = { value: halfExtent };
        shader.uniforms.kineticPanelColorA = { value: new THREE.Color(colorA) };
        shader.uniforms.kineticPanelColorB = { value: new THREE.Color(colorB) };
        shader.uniforms.kineticPanelIntensity = { value: material.emissiveIntensity };
        shader.uniforms.kineticBloomOnly = bloomOnlyUniform;
        shader.uniforms.kineticHoverGold = hoverGoldUniform;
        shader.uniforms.kineticHoverGoldColor = { value: HOVER_GOLD_COLOR };
        shader.vertexShader = shader.vertexShader
          .replace(
            '#include <common>',
            '#include <common>\nvarying vec3 vKineticLocalPosition;',
          )
          .replace(
            '#include <begin_vertex>',
            '#include <begin_vertex>\nvKineticLocalPosition = position;',
          );
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            `#include <common>
varying vec3 vKineticLocalPosition;
uniform vec3 kineticPanelCenter;
uniform vec3 kineticPanelAxis;
uniform float kineticPanelHalfExtent;
uniform vec3 kineticPanelColorA;
uniform vec3 kineticPanelColorB;
uniform vec3 kineticHoverGoldColor;
uniform float kineticPanelIntensity;
uniform float kineticBloomOnly;
uniform float kineticHoverGold;`,
          )
          .replace(
            '#include <emissivemap_fragment>',
            `#include <emissivemap_fragment>
float kineticSignedAxis = dot(vKineticLocalPosition - kineticPanelCenter, kineticPanelAxis) / kineticPanelHalfExtent;
float kineticEndpointMask = smoothstep(0.68, 0.88, abs(kineticSignedAxis));
float kineticMaxChannel = max(diffuseColor.r, max(diffuseColor.g, diffuseColor.b));
float kineticMinChannel = min(diffuseColor.r, min(diffuseColor.g, diffuseColor.b));
float kineticColorMask = smoothstep(0.08, 0.28, kineticMaxChannel - kineticMinChannel);
float kineticPanelMask = kineticEndpointMask * kineticColorMask;
vec3 kineticPanelColor = mix(kineticPanelColorA, kineticPanelColorB, step(0.0, kineticSignedAxis));
kineticPanelColor = mix(kineticPanelColor, kineticHoverGoldColor, kineticHoverGold);
float kineticMainGain = mix(0.18, 0.45, kineticBloomOnly);
kineticMainGain *= mix(1.0, 1.22, kineticHoverGold);
totalEmissiveRadiance += kineticPanelColor * kineticPanelMask * kineticPanelIntensity * kineticMainGain;
if (kineticBloomOnly > 0.5) {
  diffuseColor.rgb = vec3(0.0);
  roughnessFactor = 1.0;
  metalnessFactor = 0.0;
}`,
          );
      };
      material.customProgramCacheKey = () => `kinetic-panel-gold-hover-v2-${colorA}-${colorB}-${materialIndex}`;
      material.needsUpdate = true;
      glowMaterials.push(material);
      return material;
    });
    object.material = Array.isArray(object.material) ? tuned : tuned[0];
    object.layers.enable(BLOOM_LAYER);
  });
  return glowMaterials;
}

function installCrystalBloomIsolation(material: THREE.MeshPhysicalMaterial) {
  const bloomOnlyUniform = { value: 0 };
  material.userData.kineticBloomUniform = bloomOnlyUniform;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.kineticBloomOnly = bloomOnlyUniform;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float kineticBloomOnly;',
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
if (kineticBloomOnly > 0.5) {
  totalEmissiveRadiance *= 1.8;
  diffuseColor.rgb = vec3(0.0);
  roughnessFactor = 1.0;
  metalnessFactor = 0.0;
} else {
  totalEmissiveRadiance *= 0.24;
}`,
      );
  };
  material.customProgramCacheKey = () => 'kinetic-crystal-bloom-isolation-v1';
  material.needsUpdate = true;
}

function makeMotionPivot(root: THREE.Group, name: string, nodes: THREE.Object3D[], centerOverride?: THREE.Vector3) {
  const bounds = new THREE.Box3();
  nodes.forEach((node) => bounds.expandByObject(node));
  const center = centerOverride?.clone() ?? bounds.getCenter(new THREE.Vector3());
  const pivot = new THREE.Group();
  pivot.name = name;
  pivot.position.copy(center);
  root.add(pivot);
  nodes.forEach((node) => pivot.attach(node));
  return pivot;
}

const HOVER_GOLD = 0xd7902f;
const HOVER_GOLD_COLOR = new THREE.Color(HOVER_GOLD);
const CRYSTAL_HOVER_COLOR = new THREE.Color(0x6f3104);
const CRYSTAL_HOVER_ATTENUATION = new THREE.Color(0xff9b2f);
const CRYSTAL_HOVER_SPECULAR = new THREE.Color(0xffc15e);

function installHoverFeedback(model: KineticModel) {
  const materials = new Set<THREE.MeshStandardMaterial>();
  model.root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const entries = Array.isArray(object.material) ? object.material : [object.material];
    entries.forEach((entry) => {
      if (entry instanceof THREE.MeshStandardMaterial && !entry.userData.kineticPanelGlow) materials.add(entry);
    });
  });

  model.highlightMaterials = Array.from(materials, (material) => {
    const alreadyEmissive = material.emissive.r + material.emissive.g + material.emissive.b > 0.001;
    const isCrystalCore = material.userData.kineticCrystalCore === true;
    const baseEmissive = material.emissive.clone();
    const baseIntensity = alreadyEmissive ? material.emissiveIntensity : 0;
    material.emissiveIntensity = baseIntensity;
    material.needsUpdate = true;
    return {
      material,
      baseEmissive,
      baseIntensity,
      hoverBoost: isCrystalCore ? -0.34 : alreadyEmissive ? Math.max(0.36, baseIntensity * 0.42) : 0.018,
    };
  });

  model.root.updateWorldMatrix(true, true);
  const center = new THREE.Box3().setFromObject(model.root).getCenter(new THREE.Vector3());
  model.root.worldToLocal(center);
  const light = new THREE.PointLight(HOVER_GOLD, 0, 2.75, 2.35);
  light.name = `${model.id}_HoverLight`;
  light.position.copy(center);
  light.position.y += 0.25 / Math.max(model.root.scale.y, 0.001);
  light.position.z += 0.75 / Math.max(model.root.scale.z, 0.001);
  model.root.add(light);
  model.hoverLight = light;
  model.hoverAmount = 0;
}

function fitImportedModel(
  root: THREE.Group,
  targetHeight: number,
  centerX: number,
  bottomY: number,
  centerZ: number,
) {
  root.updateMatrixWorld(true);
  const initial = new THREE.Box3().setFromObject(root);
  const height = Math.max(initial.max.y - initial.min.y, 0.001);
  root.scale.setScalar(targetHeight / height);
  root.updateMatrixWorld(true);
  const fitted = new THREE.Box3().setFromObject(root);
  const center = fitted.getCenter(new THREE.Vector3());
  root.position.set(centerX - center.x, bottomY - fitted.min.y, centerZ - center.z);
  root.updateMatrixWorld(true);
}

function applyResponsiveModelLayout(models: KineticModel[], viewportWidth: number) {
  const layout = getResponsiveModelLayout(viewportWidth);
  models.forEach((model) => {
    model.root.updateWorldMatrix(true, true);
    const bounds = new THREE.Box3().setFromObject(model.root);
    const center = bounds.getCenter(new THREE.Vector3());
    model.root.position.x += layout[model.id] - center.x;
    model.root.updateWorldMatrix(true, true);
    const fitted = new THREE.Box3().setFromObject(model.root);
    const fittedCenter = fitted.getCenter(new THREE.Vector3());
    model.hotspotAnchor = new THREE.Vector3(layout[model.id], fitted.min.y, fittedCenter.z);
  });
}

function cloneAndTuneStandardMaterials(
  node: THREE.Object3D,
  tune: (material: THREE.MeshStandardMaterial) => void,
) {
  const tunedMaterials: THREE.MeshStandardMaterial[] = [];
  node.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const source = Array.isArray(object.material) ? object.material : [object.material];
    const tuned = source.map((entry) => {
      if (!(entry instanceof THREE.MeshStandardMaterial)) return entry;
      const material = entry.clone();
      tune(material);
      material.needsUpdate = true;
      tunedMaterials.push(material);
      return material;
    });
    object.material = Array.isArray(object.material) ? tuned : tuned[0];
  });
  return tunedMaterials;
}

function tuneTexturedMetal(
  node: THREE.Object3D,
  fallbackColor: number,
  metalness: number,
  roughness: number,
  envMapIntensity: number,
  mapTint = 0xffffff,
) {
  return cloneAndTuneStandardMaterials(node, (material) => {
    if (material.map) material.color.set(mapTint);
    else material.color.set(fallbackColor);
    material.metalness = metalness;
    material.roughness = roughness;
    material.envMapIntensity = envMapIntensity;
  });
}

function findFirstStandardMaterial(node: THREE.Object3D): THREE.MeshStandardMaterial | null {
  let found: THREE.MeshStandardMaterial | null = null;
  node.traverse((object) => {
    if (!found && object instanceof THREE.Mesh && object.material instanceof THREE.MeshStandardMaterial) {
      found = object.material;
    }
  });
  return found as THREE.MeshStandardMaterial | null;
}

function tuneRobotMaterials(root: THREE.Group) {
  tuneTexturedMetal(findRequiredNode(root, 'part_1'), 0xd4af37, 0.9, 0.2, 1.35, 0xc89b43);
  tuneTexturedMetal(findRequiredNode(root, 'part_6'), 0x1f1f22, 0.72, 0.34, 1.0, 0x24262a)
    .forEach((material) => installNeutralGoldRemap(material, 0.62));

  const brassDetailNames = ['part_0', 'part_2', 'part_3', 'part_4', 'part_7', 'part_8', 'part_9', 'part_10'];
  brassDetailNames.forEach((name) => {
    tuneTexturedMetal(findRequiredNode(root, name), 0xd4af37, 0.9, 0.21, 1.35, 0xc89b43);
  });

  const face = findRequiredNode(root, 'part_5');
  const glowMaterials: THREE.MeshStandardMaterial[] = [];
  cloneAndTuneStandardMaterials(face, (material) => {
    material.color.set(0xffffff);
    material.metalness = 0.18;
    material.roughness = 0.24;
    material.envMapIntensity = 1.2;
    material.emissive.set(0xfff3b0);
    material.emissiveMap = material.map;
    material.emissiveIntensity = 1.2;
    glowMaterials.push(material);
  });
  return glowMaterials;
}

async function loadContractModel(loader: GLTFLoader, id: ModelId, url: string): Promise<KineticModel> {
  const gltf = await loader.loadAsync(url);
  const root = gltf.scene as THREE.Group;
  const glowMaterials: THREE.MeshStandardMaterial[] = [];
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.castShadow = true;
    object.receiveShadow = true;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.forEach((entry) => {
      if (entry instanceof THREE.MeshStandardMaterial && entry.emissive.getHex() !== 0) glowMaterials.push(entry);
    });
  });
  tagModel(root, id);

  if (id === 'robot') {
    const propeller = makeMotionPivot(root, 'Propeller_Rotor', [findRequiredNode(root, 'part_1')]);
    glowMaterials.push(...tuneRobotMaterials(root));
    tuneRobotMaterials(root);
    fitImportedModel(root, 2.15, 1.12, -1.38, 0.18);
    return { id, root, parts: { propeller }, glowMaterials, baseSpeed: 1.15, speed: 1, targetSpeed: 1, phase: 0 };
  }

  if (id === 'orbital') {
    const lowerNode = findRequiredNode(root, 'part_0');
    const middleNode = findRequiredNode(root, 'part_1');
    const fixedNode = findRequiredNode(root, 'part_2');
    const upperNode = findRequiredNode(root, 'part_3');

    tuneOrbitalFixedMaterials(fixedNode);
    glowMaterials.push(
      ...installOrbitalPanelGlow(upperNode, 0x00f0ff, 0xbf00ff),
      ...installOrbitalPanelGlow(middleNode, 0xffaa00, 0x00f0ff),
      ...installOrbitalPanelGlow(lowerNode, 0xbf00ff, 0xffaa00),
    );

    const fixedCenter = new THREE.Box3().setFromObject(fixedNode).getCenter(new THREE.Vector3());
    const makeSharedAxisPivot = (name: string, node: THREE.Object3D) => {
      const nodeCenter = new THREE.Box3().setFromObject(node).getCenter(new THREE.Vector3());
      return makeMotionPivot(
        root,
        name,
        [node],
        new THREE.Vector3(fixedCenter.x, nodeCenter.y, fixedCenter.z),
      );
    };
    const lower = makeSharedAxisPivot('Rotor_Lower', lowerNode);
    const middle = makeSharedAxisPivot('Rotor_Middle', middleNode);
    const upper = makeSharedAxisPivot('Rotor_Upper', upperNode);

    // Keep a hard reference to the fixed node so contract errors are caught,
    // even though fixed geometry intentionally receives no animation.
    fixedNode.name = 'Orbital_Fixed';
    enableShadows(root);
    fitImportedModel(root, 2.86, 4.72, -1.22, -0.08);
    return {
      id,
      root,
      parts: { upper, middle, lower },
      glowMaterials,
      baseSpeed: 0.58,
      speed: 1,
      targetSpeed: 1,
      phase: 3.1,
    };
  }

  const outerNode = findRequiredNode(root, 'part_0');
  const innerNode = findRequiredNode(root, 'part_1');
  const innerDetail = findRequiredNode(root, 'part_2');
  const crystalNode = findRequiredNode(root, 'part_3');
  const baseNode = findRequiredNode(root, 'part_4');
  const middleNode = findRequiredNode(root, 'part_5');
  const crystalSource = findFirstStandardMaterial(crystalNode);
  const crystalMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x8555f8,
    emissive: 0x5a18b8,
    emissiveIntensity: 1.2,
    metalness: 0.04,
    roughness: 0.025,
    transmission: 0.96,
    thickness: 2.3,
    ior: 1.76,
    dispersion: 0.22,
    iridescence: 0.18,
    iridescenceIOR: 1.35,
    iridescenceThicknessRange: [120, 460],
    clearcoat: 1.0,
    clearcoatRoughness: 0.02,
    transparent: true,
    opacity: 1.0,
    depthWrite: false,
    flatShading: true,
    side: THREE.FrontSide,
    envMapIntensity: 1.85,
    attenuationColor: new THREE.Color(0x9d4edd),
    attenuationDistance: 12,
    toneMapped: true,
  });
  crystalMaterial.color.set(0x716cff);
  crystalMaterial.userData.kineticCrystalCore = true;
  crystalMaterial.userData.kineticCrystalBaseColor = crystalMaterial.color.clone();
  crystalMaterial.userData.kineticCrystalBaseAttenuation = crystalMaterial.attenuationColor.clone();
  crystalMaterial.specularColor.set(0xdfb4ff);
  crystalMaterial.userData.kineticCrystalBaseSpecular = crystalMaterial.specularColor.clone();
  crystalMaterial.specularIntensity = 1.6;
  crystalMaterial.map = crystalSource?.map ?? null;
  crystalMaterial.normalMap = crystalSource?.normalMap ?? null;
  crystalMaterial.normalScale.copy(crystalSource?.normalScale ?? new THREE.Vector2(1, 1));
  crystalNode.scale.multiplyScalar(0.78);
  installCrystalBloomIsolation(crystalMaterial);
  // Hunyuan's thin ring shells contain mixed winding directions. Rendering
  // both sides prevents segments from disappearing as the pivots rotate.
  tuneTexturedMetal(outerNode, 0xd4af37, 0.92, 0.19, 1.3, 0xe6bd73).forEach((material) => { material.side = THREE.DoubleSide; });
  tuneTexturedMetal(middleNode, 0xa5652a, 0.92, 0.2, 1.28, 0xc4825d).forEach((material) => { material.side = THREE.DoubleSide; });
  tuneTexturedMetal(innerNode, 0x6f4424, 0.9, 0.21, 1.25, 0x9aa0a5).forEach((material) => { material.side = THREE.DoubleSide; });
  tuneTexturedMetal(innerDetail, 0xd4af37, 0.92, 0.2, 1.28, 0xc6a35e).forEach((material) => { material.side = THREE.DoubleSide; });
  setNodeMaterial(crystalNode, crystalMaterial);
  enableBloom(crystalNode);
  // Keep the textured black/brass base, with refined matte graphite roughness
  baseNode.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !(object.material instanceof THREE.MeshStandardMaterial)) return;
    const baseMaterial = object.material.clone();
    if (baseMaterial.map) baseMaterial.color.set(0x35312d);
    baseMaterial.metalness = 0.82;
    baseMaterial.roughness = 0.48;
    baseMaterial.envMapIntensity = 0.62;
    installNeutralGoldRemap(baseMaterial, 0.5);
    object.material = baseMaterial;
  });

  const outer = makeMotionPivot(root, 'Ring_Outer', [outerNode]);
  const middle = makeMotionPivot(root, 'Ring_Middle', [middleNode]);
  const inner = makeMotionPivot(root, 'Ring_Inner', [innerNode, innerDetail]);
  const crystal = makeMotionPivot(root, 'Crystal_Core', [crystalNode]);
  // The source crystal sits slightly below the gyroscope's rotational center.
  // Align pivots in code so the crystal remains centered without editing GLB geometry.
  crystal.position.y = inner.position.y;
  const crystalLight = new THREE.PointLight(0xaa55ff, 0.6, 2.6, 2);
  crystalLight.name = 'Crystal_Internal_Light';
  crystalLight.userData.baseColor = crystalLight.color.clone();
  crystalLight.layers.enable(BLOOM_LAYER);
  crystal.add(crystalLight);
  glowMaterials.push(crystalMaterial);
  fitImportedModel(root, 3.08, 2.92, -1.42, 0.08);
  return {
    id,
    root,
    parts: { outer, middle, inner, crystal },
    glowMaterials,
    baseSpeed: 0.64,
    speed: 1,
    targetSpeed: 1,
    phase: 1.7,
  };
}

function disposeObject(root: THREE.Object3D) {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    meshMaterials.forEach((material) => {
      materials.add(material);
      Object.values(material).forEach((value) => {
        if (value instanceof THREE.Texture) textures.add(value);
      });
    });
  });
  geometries.forEach((geometry) => geometry.dispose());
  materials.forEach((material) => material.dispose());
  textures.forEach((texture) => texture.dispose());
}

export default function KineticHero({ onLoadProgress }: KineticHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const hotspotRefs = useRef<Record<ModelId, HTMLImageElement | null>>({
    robot: null,
    gyroscope: null,
    orbital: null,
  });
  const [fallbackPoster, setFallbackPoster] = useState(false);
  const [activeModel, setActiveModel] = useState<ModelId | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    const section = sectionRef.current;
    if (!mount || !section) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch {
      setFallbackPoster(true);
      onLoadProgress?.(100);
      return;
    }

    const isMobile = window.matchMedia('(max-width: 700px)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scene = new THREE.Scene();
    scene.background = null;
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    const clock = new THREE.Clock();
    const loader = new GLTFLoader();
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2(2, 2);
    const models: KineticModel[] = [];
    let frame = 0;
    let isVisible = true;
    let isDisposed = false;
    let hasFatalFallback = false;
    let selectedTouchModel: ModelId | null = null;
    let modelTasks: Promise<KineticModel>[] = [];

    const activateFallback = () => {
      if (isDisposed || hasFatalFallback) return;
      hasFatalFallback = true;
      isVisible = false;
      window.cancelAnimationFrame(frame);
      setFallbackPoster(true);
      onLoadProgress?.(100);
    };

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    const initialPixelRatio = Math.min(window.devicePixelRatio, isMobile ? 1.35 : 1.75);
    renderer.setPixelRatio(initialPixelRatio);
    renderer.domElement.className = 'kinetic-hero-canvas';
    renderer.domElement.setAttribute('aria-label', '三个可交互机械装置：机器人、行星陀螺仪与双环旋转机械');
    mount.appendChild(renderer.domElement);

    const roomEnvironment = new RoomEnvironment();
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const environmentTarget = pmremGenerator.fromScene(roomEnvironment, 0.04);
    roomEnvironment.dispose();
    pmremGenerator.dispose();
    scene.environment = environmentTarget.texture;
    scene.environmentIntensity = 0.56;

    const bloomComposer = new EffectComposer(renderer);
    bloomComposer.renderToScreen = false;
    bloomComposer.setPixelRatio(initialPixelRatio);
    bloomComposer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.35, 0.45, 0.82);
    bloomPass.threshold = 0.82;
    bloomPass.strength = 0.35;
    bloomPass.radius = 0.45;
    bloomComposer.addPass(bloomPass);

    const finalComposer = new EffectComposer(renderer);
    finalComposer.setPixelRatio(initialPixelRatio);
    finalComposer.addPass(new RenderPass(scene, camera));
    const combinePass = new ShaderPass(new THREE.ShaderMaterial({
      uniforms: {
        baseTexture: { value: null },
        bloomTexture: { value: bloomComposer.renderTarget2.texture },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D baseTexture;
        uniform sampler2D bloomTexture;
        varying vec2 vUv;
        void main() {
          gl_FragColor = texture2D(baseTexture, vUv) + vec4(texture2D(bloomTexture, vUv).rgb, 0.0);
        }
      `,
      depthWrite: false,
      depthTest: false,
    }), 'baseTexture');
    finalComposer.addPass(combinePass);
    const outputPass = new OutputPass();
    finalComposer.addPass(outputPass);

    const ambient = new THREE.HemisphereLight(0x82c5e8, 0x2b1d12, 0.6);
    scene.add(ambient);
    const warmSpot = new THREE.SpotLight(0xffc778, 24, 24, Math.PI / 5.2, 0.66, 1.2);
    warmSpot.position.set(5.8, 7.2, 5.6);
    warmSpot.target.position.set(1.3, -0.5, 0);
    warmSpot.castShadow = true;
    warmSpot.shadow.mapSize.set(1024, 1024);
    warmSpot.shadow.bias = -0.00012;
    warmSpot.shadow.normalBias = 0.025;
    warmSpot.shadow.radius = 4;
    scene.add(warmSpot, warmSpot.target);
    const coolFill = new THREE.DirectionalLight(0x6eacd2, 0.56);
    coolFill.position.set(-5, 2.6, 4);
    scene.add(coolFill);
    const lampLight = new THREE.DirectionalLight(0xffeed1, 1.45);
    lampLight.position.set(5, 6, 4);
    scene.add(lampLight);
    const orbitalRim = new THREE.SpotLight(0xb9dcff, 5.0, 16, Math.PI / 7, 0.82, 1.6);
    orbitalRim.position.set(8.6, 4.8, -4.2);
    orbitalRim.target.position.set(DESKTOP_MODEL_X.orbital, 0.1, -0.08);
    scene.add(orbitalRim, orbitalRim.target);

    const shadowPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(9.5, 4.4),
      new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.32 }),
    );
    shadowPlane.name = 'Blueprint_Shadow_Receiver';
    shadowPlane.rotation.x = -Math.PI / 2;
    shadowPlane.position.set(1.5, -1.44, 0.35);
    shadowPlane.receiveShadow = true;
    scene.add(shadowPlane);

    const syncHotspots = () => {
      camera.updateMatrixWorld();
      models.forEach((model) => {
        const icon = hotspotRefs.current[model.id];
        if (!icon || !model.hotspotAnchor) return;
        const projected = model.hotspotAnchor.clone().project(camera);
        const xPercent = THREE.MathUtils.clamp((projected.x * 0.5 + 0.5) * 100, 0, 100);
        icon.style.setProperty('--icon-x', `${xPercent.toFixed(3)}%`);
        icon.dataset.projectedX = xPercent.toFixed(3);
        icon.dataset.bound = 'true';
      });
    };

    const updateCamera = () => {
      const width = mount.clientWidth || window.innerWidth;
      const height = mount.clientHeight || window.innerHeight;
      camera.aspect = width / height;

      if (width <= 700) {
        // 手机竖屏自适应计算：
        // 模型中心在 2.60 处，根据竖屏纵横比动态自适应视距，保证三模型全部稳妥在视野中央
        const targetCenterX = 2.60;
        const mobileAspect = Math.max(0.38, camera.aspect);
        const dynamicZ = Math.max(18.5, 9.6 / mobileAspect);
        camera.position.set(targetCenterX, 3.85, dynamicZ);
        const lookAt = new THREE.Vector3(targetCenterX, -0.15, 0);
        camera.lookAt(lookAt);
      } else {
        const compactBlend = THREE.MathUtils.smoothstep(width, 700, 1100);
        const wideBlend = THREE.MathUtils.smoothstep(width, 1500, 1700);
        const lookAt = new THREE.Vector3(3.02, -0.05, 0).lerp(new THREE.Vector3(2.0, -0.02, 0), compactBlend);
        camera.position
          .set(2.72, 4.25, 18.5)
          .lerp(new THREE.Vector3(1.25, 3.15, 11.5), compactBlend)
          .lerp(new THREE.Vector3(1.1, 3.05, 10.65), wideBlend);
        lookAt.lerp(new THREE.Vector3(2.0, -0.02, 0), wideBlend);
        camera.lookAt(lookAt);
      }
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
      if (models.length) applyResponsiveModelLayout(models, width);
      orbitalRim.target.position.x = getResponsiveModelLayout(width).orbital;
      orbitalRim.target.updateMatrixWorld();
      const pixelRatio = Math.min(window.devicePixelRatio, width <= 700 ? 1.35 : 1.75);
      renderer.setPixelRatio(pixelRatio);
      renderer.setSize(width, height, false);
      bloomComposer.setPixelRatio(pixelRatio);
      bloomComposer.setSize(width, height);
      finalComposer.setPixelRatio(pixelRatio);
      finalComposer.setSize(width, height);
      syncHotspots();
    };
    updateCamera();
    const mountResizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateCamera);
    mountResizeObserver?.observe(mount);

    onLoadProgress?.(55);

    // Slow connections must not discard models that are still downloading.
    // Release the page loader while keeping the live navigation available.
    const loadingGraceTimer = window.setTimeout(() => {
      if (!isDisposed) onLoadProgress?.(100);
    }, MODEL_LOADING_GRACE_MS);

    const installModels = async () => {
      modelTasks = [
        loadContractModel(loader, 'robot', MODEL_ASSETS.robot),
        loadContractModel(loader, 'gyroscope', MODEL_ASSETS.gyroscope),
        loadContractModel(loader, 'orbital', MODEL_ASSETS.orbital),
      ];
      try {
        const result = await Promise.all(modelTasks);
        if (isDisposed || hasFatalFallback) {
          result.forEach((model) => disposeObject(model.root));
          return;
        }
        result.forEach((model) => {
          installHoverFeedback(model);
          models.push(model);
          scene.add(model.root);
        });
        applyResponsiveModelLayout(models, mount.clientWidth || window.innerWidth);
        syncHotspots();
        onLoadProgress?.(100);
      } catch {
        activateFallback();
        void Promise.allSettled(modelTasks).then((results) => {
          results.forEach((result) => {
            if (result.status === 'fulfilled') disposeObject(result.value.root);
          });
        });
      } finally {
        window.clearTimeout(loadingGraceTimer);
      }
    };
    void installModels();

    const setHoveredModel = (id: ModelId | null) => {
      setActiveModel(id);
      renderer.domElement.dataset.activeModel = id ?? '';
      models.forEach((model) => {
        const active = model.id === id;
        model.targetSpeed = active ? 3.1 : 1;
      });
    };

    const pickModel = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(models.map((model) => model.root), true)[0];
      return (hit?.object.userData.kineticModelId as ModelId | undefined) ?? null;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      setHoveredModel(pickModel(event));
    };
    const handlePointerLeave = () => {
      if (!selectedTouchModel) setHoveredModel(null);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      const picked = pickModel(event);
      selectedTouchModel = selectedTouchModel === picked ? null : picked;
      setHoveredModel(selectedTouchModel);
    };
    renderer.domElement.addEventListener('pointermove', handlePointerMove, { passive: true });
    renderer.domElement.addEventListener('pointerleave', handlePointerLeave);
    renderer.domElement.addEventListener('pointerdown', handlePointerDown, { passive: true });

    const animateModel = (model: KineticModel, delta: number, elapsed: number) => {
      model.speed = THREE.MathUtils.damp(model.speed, model.targetSpeed, 5.2, delta);
      const speed = reduceMotion ? 0 : model.baseSpeed * model.speed * delta;
      if (model.id === 'robot') {
        model.parts.propeller!.rotation.y += speed * 2.4;
      } else if (model.id === 'gyroscope') {
        model.parts.outer!.rotation.z += speed * 0.48;
        model.parts.middle!.rotation.x -= speed * 0.82;
        model.parts.inner!.rotation.y += speed * 1.12;
        const crystal = model.parts.crystal!;
        crystal.rotation.y += speed * 0.66;
      } else {
        model.parts.upper!.rotation.y += speed * 1.08;
        model.parts.middle!.rotation.y -= speed * 0.82;
        model.parts.lower!.rotation.y += speed * 0.58;
      }
      const active = model.targetSpeed > 1 ? 1 : 0;
      model.hoverAmount = THREE.MathUtils.damp(model.hoverAmount ?? 0, active, 7.5, delta);
      const hoverAmount = model.hoverAmount;
      if (model.hoverLight) {
        const lightPeak = model.id === 'robot' ? 0.82 : model.id === 'gyroscope' ? 0.62 : 1.0;
        model.hoverLight.intensity = hoverAmount * (lightPeak + Math.sin(elapsed * 4.2 + model.phase) * 0.06);
      }
      model.highlightMaterials?.forEach(({ material, baseEmissive, baseIntensity, hoverBoost }, index) => {
        material.emissive.copy(baseEmissive).lerp(HOVER_GOLD_COLOR, hoverAmount);
        if (material.userData.kineticCrystalCore === true && material instanceof THREE.MeshPhysicalMaterial) {
          material.color.copy(material.userData.kineticCrystalBaseColor as THREE.Color).lerp(CRYSTAL_HOVER_COLOR, hoverAmount);
          material.attenuationColor.copy(material.userData.kineticCrystalBaseAttenuation as THREE.Color).lerp(CRYSTAL_HOVER_ATTENUATION, hoverAmount);
          material.specularColor.copy(material.userData.kineticCrystalBaseSpecular as THREE.Color).lerp(CRYSTAL_HOVER_SPECULAR, hoverAmount);
        }
        const pulse = 1 + Math.sin(elapsed * 4.6 + model.phase + index * 0.7) * 0.05;
        const target = baseIntensity + hoverAmount * hoverBoost * pulse;
        material.emissiveIntensity = THREE.MathUtils.damp(material.emissiveIntensity, target, 10, delta);
      });
      model.glowMaterials.forEach((material) => {
        const hoverGoldUniform = material.userData.kineticHoverGoldUniform as { value: number } | undefined;
        if (hoverGoldUniform) hoverGoldUniform.value = THREE.MathUtils.damp(hoverGoldUniform.value, hoverAmount, 9, delta);
      });
      if (model.id === 'gyroscope') {
        const crystalLight = model.parts.crystal?.getObjectByName('Crystal_Internal_Light');
        if (crystalLight instanceof THREE.PointLight) {
          const baseColor = crystalLight.userData.baseColor as THREE.Color;
          crystalLight.color.copy(baseColor).lerp(HOVER_GOLD_COLOR, hoverAmount);
          crystalLight.intensity = 0.35 + hoverAmount * 0.08;
        }
      }
    };

    const setBloomIsolation = (enabled: boolean) => {
      models.forEach((model) => {
        model.glowMaterials.forEach((material) => {
          const uniform = material.userData.kineticBloomUniform as { value: number } | undefined;
          if (uniform) uniform.value = enabled ? 1 : 0;
        });
      });
    };

    const renderFrame = () => {
      if (!isVisible || isDisposed || hasFatalFallback) return;
      frame = window.requestAnimationFrame(renderFrame);
      const delta = Math.min(clock.getDelta(), 0.05);
      const elapsed = clock.elapsedTime;
      models.forEach((model) => animateModel(model, delta, elapsed));
      camera.layers.set(BLOOM_LAYER);
      setBloomIsolation(true);
      try {
        bloomComposer.render();
      } finally {
        setBloomIsolation(false);
      }
      camera.layers.set(0);
      finalComposer.render();
    };
    renderFrame();

    const observer = new IntersectionObserver(([entry]) => {
      if (hasFatalFallback) return;
      const nextVisible = entry.isIntersecting;
      if (nextVisible === isVisible) return;
      isVisible = nextVisible;
      if (isVisible) {
        clock.getDelta();
        renderFrame();
      } else {
        window.cancelAnimationFrame(frame);
      }
    }, { threshold: 0.02 });
    observer.observe(section);

    window.addEventListener('resize', updateCamera);
    return () => {
      isDisposed = true;
      window.clearTimeout(loadingGraceTimer);
      observer.disconnect();
      mountResizeObserver?.disconnect();
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateCamera);
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      models.forEach((model) => disposeObject(model.root));
      shadowPlane.geometry.dispose();
      (shadowPlane.material as THREE.Material).dispose();
      environmentTarget.dispose();
      bloomPass.dispose();
      combinePass.dispose();
      outputPass.dispose();
      bloomComposer.dispose();
      finalComposer.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [onLoadProgress]);

  return (
    <section ref={sectionRef} id="home" className={`kinetic-hero ${fallbackPoster ? 'is-poster-fallback' : ''} ${activeModel ? 'is-model-active' : ''}`} aria-label="LINZEXIN 可交互机械实验室首屏">
      <img className="kinetic-hero-background" src={`${ASSET_ROOT}/kinetic-lab-background.png`} alt="" aria-hidden="true" />
      <div ref={mountRef} className="kinetic-hero-stage" aria-hidden={fallbackPoster} />
      {fallbackPoster && (
        <>
        <img
          className="kinetic-hero-poster"
          src={`${ASSET_ROOT}/kinetic-hero-reference-final.png`}
          alt="LINZEXIN 机械实验室作品集首屏"
          fetchPriority="high"
        />
        <div className="kinetic-fallback-links">
          <h1>LINZEXIN 作品集</h1>
          <nav aria-label="作品集导航">
            {navItems.map(([label, target]) => <a key={target} href={`#${target}`}>{label}</a>)}
          </nav>
        </div>
        </>
      )}

      {!fallbackPoster && (
        <>
          <div className="kinetic-hero-vignette" aria-hidden="true" />
          <div className="kinetic-hero-icons" aria-hidden="true">
            <img ref={(node) => { hotspotRefs.current.robot = node; }} data-model="robot" className="kinetic-model-icon kinetic-model-icon-robot" src={`${ASSET_ROOT}/icons/propeller-cursor.png`} alt="" />
            <img ref={(node) => { hotspotRefs.current.gyroscope = node; }} data-model="gyroscope" className="kinetic-model-icon kinetic-model-icon-gyroscope" src={`${ASSET_ROOT}/icons/crystal-orbit.png`} alt="" />
            <img ref={(node) => { hotspotRefs.current.orbital = node; }} data-model="orbital" className="kinetic-model-icon kinetic-model-icon-orbital" src={`${ASSET_ROOT}/icons/response-rings.png`} alt="" />
          </div>
          <div className="kinetic-hero-ui">
            <header className="kinetic-topbar">
              <a className="kinetic-brand" href="#home" aria-label="LINZEXIN 首页">
                <span>LINZE</span><span className="kinetic-brand-accent">XIN</span>
                <i aria-hidden="true">/</i><small>CREATIVE SYSTEMS</small>
              </a>
              <nav className="kinetic-nav" aria-label="首屏导航">
                {navItems.map(([label, target]) => <a key={target} href={`#${target}`}>{label}</a>)}
              </nav>
            </header>

            <div className="kinetic-hero-copy">
              <h1 className="kinetic-hero-title">
                <span className="sr-only">让视觉回应你</span>
                <div className="kinetic-title-stage">
                  {/* 动态流光光轨 (藏在字体后面，作为柔和环境光) */}
                  <svg
                    className="kinetic-title-aurora"
                    viewBox="0 0 1080 684"
                    fill="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="auroraGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.65" />
                        <stop offset="35%" stopColor="#f59e0b" stopOpacity="0.8" />
                        <stop offset="70%" stopColor="#fde68a" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.2" />
                      </linearGradient>
                      <linearGradient id="auroraGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.75" />
                        <stop offset="40%" stopColor="#fbbf24" stopOpacity="0.7" />
                        <stop offset="80%" stopColor="#d97706" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                      </linearGradient>
                      <filter id="auroraGlow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur1" />
                        <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur2" />
                        <feMerge>
                          <feMergeNode in="blur2" />
                          <feMergeNode in="blur1" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <path
                      d="M 50,310 C 110,130 260,80 390,120 C 560,170 700,320 860,260 C 940,230 1020,320 1050,350"
                      className="kinetic-aurora-path kinetic-aurora-main"
                      stroke="url(#auroraGrad1)"
                      filter="url(#auroraGlow)"
                    />
                    <path
                      d="M 60,570 C 220,620 420,580 580,530 C 740,480 880,510 1020,390"
                      className="kinetic-aurora-path kinetic-aurora-sub"
                      stroke="url(#auroraGrad2)"
                      filter="url(#auroraGlow)"
                    />
                  </svg>
                  {/* 真实矢量书法标题主体 (纯文字排版设计，非抠图) */}
                  <div className="kinetic-title-text-wrap" aria-label="让视觉回应你">
                    <span className="kinetic-title-line kinetic-title-top">让视觉</span>
                    <span className="kinetic-title-line kinetic-title-bottom">回应你</span>
                  </div>
                  {/* 动态闪烁星芒微粒 */}
                  <span className="kinetic-star-sparkle star-1" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 0 C12 7 17 12 24 12 C17 12 12 17 12 24 C12 17 7 12 0 12 C7 12 12 7 12 0 Z" fill="#fff7ed" /><circle cx="12" cy="12" r="2.5" fill="#fbbf24" /></svg>
                  </span>
                  <span className="kinetic-star-sparkle star-2" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 0 C12 7 17 12 24 12 C17 12 12 17 12 24 C12 17 7 12 0 12 C7 12 12 7 12 0 Z" fill="#fff7ed" /><circle cx="12" cy="12" r="2.5" fill="#fbbf24" /></svg>
                  </span>
                  <span className="kinetic-star-sparkle star-3" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 0 C12 7 17 12 24 12 C17 12 12 17 12 24 C12 17 7 12 0 12 C7 12 12 7 12 0 Z" fill="#fff7ed" /><circle cx="12" cy="12" r="2.5" fill="#fbbf24" /></svg>
                  </span>
                  <span className="kinetic-star-sparkle star-4" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 0 C12 7 17 12 24 12 C17 12 12 17 12 24 C12 17 7 12 0 12 C7 12 12 7 12 0 Z" fill="#fff7ed" /><circle cx="12" cy="12" r="2.5" fill="#fbbf24" /></svg>
                  </span>
                </div>
              </h1>
              <div className="kinetic-role"><span>AI视觉导演</span><i>×</i><span>工作流搭建</span></div>
              <p>用AI影像、视觉设计和自动化工作流。<br />把创意变成可交互、可复用、可交付的体验。</p>
            </div>

            <a className="kinetic-scroll-cue" href="#projects" aria-label="向下探索核心项目">
              <span aria-hidden="true"><i /></span>
              <strong>向下探索</strong>
            </a>
            <div className="kinetic-live-status" aria-live="polite">
              {activeModel ? `${activeModel === 'robot' ? '机器人' : activeModel === 'gyroscope' ? '行星陀螺仪' : '双环旋转机械'}｜加速 · 暖金高光` : '移动到模型上，感受它的响应'}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
